import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Mic, Square, Loader2, Search, Upload, Radio, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { formatApiErrorDetail, formatDuration, relTime } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import MeetingDetail from "@/components/MeetingDetail";

export default function RecordingStudio({ onTasksChanged }) {
  const [meetings, setMeetings] = useState([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [phase, setPhase] = useState("");
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startedRef = useRef(0);
  const fileRef = useRef(null);

  const load = useCallback(async (q) => {
    try {
      const r = await api.get("/meetings", { params: q ? { q } : {} });
      setMeetings(r.data);
    } catch (err) {
      console.error("Failed to load meetings:", err);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(query), query ? 350 : 0);
    return () => clearTimeout(t);
  }, [query, load]);

  useEffect(() => {
    const int = setInterval(() => load(query), 10000);
    return () => clearInterval(int);
  }, [query, load]);

  const processAudio = async (blob, filename, seconds) => {
    setPhase("Saving the recording & transcribing…");
    try {
      const form = new FormData();
      form.append("audio", blob, filename);
      form.append("duration", String(seconds || 0));
      const created = await api.post("/meetings", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhase("Writing AI notes & to-dos…");
      let meeting = created.data;
      try {
        const analyzed = await api.post(`/meetings/${meeting.id}/analyze`);
        meeting = analyzed.data;
      } catch (e) {
        toast.error(
          formatApiErrorDetail(e.response?.data?.detail) ||
            "Saved the recording, but AI notes failed — retry from the recording."
        );
      }
      setSelected(meeting);
      await load(query);
      toast.success("Recording processed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not process the recording");
    } finally {
      setPhase("");
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      let mime = "audio/webm;codecs=opus";
      if (!window.MediaRecorder?.isTypeSupported?.(mime)) {
        mime = MediaRecorder.isTypeSupported?.("audio/webm") ? "audio/webm" : "";
      }
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (ev) => ev.data.size > 0 && chunksRef.current.push(ev.data);
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const secs = Math.round((Date.now() - startedRef.current) / 1000);
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        processAudio(blob, "meeting.webm", secs);
      };
      mr.start();
      mediaRef.current = mr;
      startedRef.current = Date.now();
      setElapsed(0);
      setRecording(true);
      timerRef.current = setInterval(
        () => setElapsed(Math.round((Date.now() - startedRef.current) / 1000)),
        500
      );
    } catch {
      toast.error("Microphone access denied. You can upload an audio file instead.");
    }
  };

  const stopRecording = () => {
    clearInterval(timerRef.current);
    mediaRef.current?.stop();
    setRecording(false);
  };

  const onFile = (f) => {
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) {
      toast.error("Audio too large (max 25MB)");
      return;
    }
    processAudio(f, f.name || "upload.m4a", 0);
  };

  const onDelete = async (id) => {
    setMeetings((m) => m.filter((x) => x.id !== id));
    if (selected?.id === id) setSelected(null);
    await api.delete(`/meetings/${id}`).catch(() => {});
  };

  const busy = Boolean(phase);

  return (
    <div className="flex-1 min-w-0 flex bg-[#16181F]" data-testid="recording-studio">
      {/* Archive list + recorder */}
      <div
        className={`${selected ? "hidden lg:flex" : "flex"} flex-col w-full lg:w-[360px] shrink-0 border-r border-[#2E3242] bg-[#121318] min-h-0`}
      >
        <div className="p-4 border-b border-[#2E3242] shrink-0">
          {recording ? (
            <div className="rounded-2xl border border-red-800/60 bg-red-950/30 p-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <span className="w-3 h-3 rounded-full bg-red-500 pulse-glow" />
                <span className="font-mono text-2xl text-red-200" data-testid="record-timer">
                  {formatDuration(elapsed)}
                </span>
              </div>
              <p className="text-xs text-gray-400 mb-3">Recording the meeting… speak freely</p>
              <Button
                data-testid="stop-recording-button"
                onClick={stopRecording}
                className="w-full bg-red-600 hover:bg-red-500 text-white h-11"
              >
                <Square className="w-4 h-4 mr-1.5" /> Stop & process
              </Button>
            </div>
          ) : busy ? (
            <div
              className="rounded-2xl border border-red-900/40 bg-[#1E1013]/70 p-4 flex items-center gap-3"
              data-testid="studio-processing"
            >
              <Loader2 className="w-5 h-5 animate-spin text-red-400 shrink-0" />
              <p className="text-sm text-gray-300">{phase}</p>
            </div>
          ) : (
            <div className="space-y-2">
              <Button
                data-testid="start-recording-button"
                onClick={startRecording}
                className="w-full ruby-gradient text-white border border-red-900/40 h-12 text-sm font-semibold ember-glow"
              >
                <Mic className="w-5 h-5 mr-2" /> Record a meeting
              </Button>
              <input
                ref={fileRef}
                type="file"
                accept="audio/*,.mp3,.wav,.m4a,.webm,.mp4,.mpga,.mpeg"
                className="hidden"
                data-testid="studio-file-input"
                onChange={(e) => {
                  onFile(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
              <button
                data-testid="studio-upload-button"
                onClick={() => fileRef.current?.click()}
                className="w-full flex items-center justify-center gap-2 h-10 rounded-lg bg-[#1E2029] border border-[#2E3242] text-sm text-gray-400 hover:text-white hover:border-red-900/40 transition-colors"
              >
                <Upload className="w-4 h-4" /> Upload existing audio
              </button>
            </div>
          )}
        </div>

        <div className="p-3 border-b border-[#2E3242] shrink-0 relative">
          <Search className="w-4 h-4 text-gray-500 absolute left-6 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="meeting-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search transcripts, notes, tasks…"
            className="bg-[#16181F] border-[#2E3242] focus-visible:ring-red-600 h-10 pl-9 text-sm"
          />
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="meeting-archive-list">
          {meetings.length === 0 && (
            <div className="text-center pt-10 px-4">
              <div className="w-12 h-12 rounded-2xl ruby-gradient flex items-center justify-center mx-auto mb-3">
                <Radio className="w-5 h-5 text-white" />
              </div>
              <p className="text-sm text-gray-400 font-semibold">
                {query ? "No recordings match that search" : "The archive is empty"}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                {query ? "Try another keyword." : "Hit record and the AI will do the rest."}
              </p>
            </div>
          )}
          {meetings.map((m) => (
            <motion.button
              key={m.id}
              layout
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid={`meeting-item-${m.id}`}
              onClick={async () => {
                const r = await api.get(`/meetings/${m.id}`).catch(() => null);
                setSelected(r ? r.data : m);
              }}
              className={`w-full text-left rounded-xl border p-3 transition-colors ${
                selected?.id === m.id
                  ? "bg-red-950/40 border-red-900/50"
                  : "bg-[#1E2029] border-[#2E3242] hover:border-red-900/40"
              }`}
            >
              <p className="text-sm font-semibold text-gray-100 leading-snug line-clamp-2">{m.title}</p>
              <p className="text-[11px] text-gray-500 mt-1">
                {m.recorded_by} · {relTime(m.created_at)}
                {m.duration ? ` · ${formatDuration(m.duration)}` : ""}
              </p>
              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                {m.status === "analyzed" ? (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-950/60 text-red-300 border border-red-900/40 flex items-center gap-1">
                    <Sparkles className="w-2.5 h-2.5" /> AI notes
                  </span>
                ) : (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-950/40 text-amber-300 border border-amber-800/50">
                    transcript only
                  </span>
                )}
                {m.todos?.length > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-950/40 text-emerald-300 border border-emerald-900/40">
                    {m.todos.length} to-dos
                  </span>
                )}
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {selected ? (
        <MeetingDetail
          meeting={selected}
          onBack={() => setSelected(null)}
          onChanged={(m) => {
            setSelected(m);
            load(query);
          }}
          onDelete={onDelete}
          onTasksChanged={onTasksChanged}
        />
      ) : (
        <div className="hidden lg:flex flex-1 flex-col items-center justify-center text-center px-8">
          <div className="w-16 h-16 rounded-2xl ruby-gradient flex items-center justify-center mb-4 ember-glow">
            <Mic className="w-7 h-7 text-white" />
          </div>
          <p className="font-display font-bold text-lg text-gray-100">Recording Studio</p>
          <p className="text-sm text-gray-500 mt-1.5 max-w-md">
            Record a meeting from any phone or laptop. Everyone on the team gets the raw voice note,
            AI-written notes, and a to-do list you can post straight to the Task Board.
          </p>
        </div>
      )}
    </div>
  );
}
